import { useEffect, useState } from "react";
import DashboardShell from "@/components/dashboard/DashboardShell";
import {
  EcbFxNotice,
  EcbFxStatus,
  FxHistoryChart,
  FxRateCard,
} from "@/features/ecbFx/EcbFxDashboardParts";
import { useEcbFxMarket } from "@/features/ecbFx/useEcbFxMarket";
import type { FxCurrency } from "@/features/ecbFx/types";

export default function EcbFxDashboard() {
  const { data, loading, refreshing, error, refresh } = useEcbFxMarket();
  const [currency, setCurrency] = useState<FxCurrency>("USD");

  useEffect(() => {
    document.title = "Devisen · CashFUX";
  }, []);

  const selectedPair = data?.pairs.find((pair) => pair.currency === currency) ?? null;

  return (
    <DashboardShell
      title="Devisen"
      subtitle="Offizielle EZB-Referenzkurse"
    >
      <div className="market-dashboard text-white">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold">Wechselkurse</h1>
            <p className="mt-1 text-sm text-white/40">EUR gegenüber den wichtigsten Handelswährungen</p>
          </div>
          {data && <EcbFxStatus data={data} refreshing={refreshing} refresh={() => void refresh()} />}
        </div>
        <div className="mt-6">
          <EcbFxNotice />
        </div>
        {loading && (
          <div className="mt-8 grid animate-pulse gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-32 border border-white/10 bg-[#07080d]" />
            ))}
          </div>
        )}
        {error && (
          <div className="mt-8 border-l-4 border-[#e01e37] bg-[#07080d] p-5">
            <p className="font-semibold">Devisenkurse nicht verfügbar</p>
            <p className="mt-1 text-sm text-white/40">{error}</p>
            <button
              onClick={() => void refresh()}
              className="mt-4 h-10 bg-[#e01e37] px-4 text-sm font-semibold text-white"
            >
              Erneut versuchen
            </button>
          </div>
        )}
        {data && (
          <div className="mt-8 space-y-6">
            <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
              {data.pairs.map((pair) => (
                <FxRateCard
                  key={pair.currency}
                  pair={pair}
                  selected={pair.currency === currency}
                  onSelect={() => setCurrency(pair.currency)}
                />
              ))}
            </section>
            {!data.pairs.length && (
              <div className="border border-dashed border-white/10 bg-[#07080d] p-10 text-center text-white/40">
                Keine Devisenkurse verfügbar.
              </div>
            )}
            {selectedPair && <FxHistoryChart pair={selectedPair} />}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
